import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import axios from '../axios';
import KpiCardGroup from './shared/KpiCardGroup';
import TimeSeriesChart from './shared/TimeSeriesChart';
import ParticipantDetail from './event/ParticipantDetail';
import PrPointSection from './event/PrPointSection';
import PastEventComparison from './event/PastEventComparison';

const CHART_TABS = [
  { key: 'pv', label: 'PV・参加推移' },
  { key: 'pr', label: 'PRポイント効率' },
];

const EventDashboard = ({ eventId, circleId, purchaseUrl }) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeChart, setActiveChart] = useState('pv');

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await axios.get(`/api/dashboard/event/${eventId}`);
      setData(res.data);
    } catch (e) {
      console.error('Dashboard fetch error:', e);
      setError('データの取得に失敗しました。ページを再読み込みしてください。');
    }
    setIsLoading(false);
  }, [eventId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (isLoading) {
    return <div className="o-dashboard-loading">読み込み中...</div>;
  }

  if (error || !data) {
    return (
      <div className="o-dashboard-loading">
        <div>
          <p>{error || 'データを取得できませんでした。'}</p>
          <button type="button" onClick={fetchData} className="a-button a-button--primary">
            再読み込み
          </button>
        </div>
      </div>
    );
  }

  const { event, kpi, charts, participants, pr_point, past_events } = data;

  const kpiCards = [
    { label: 'PV', value: kpi.pageviews.value, diff: kpi.pageviews.diff },
    { label: '参加者', value: kpi.participants.value, diff: kpi.participants.diff },
    { label: 'CVR', value: kpi.cvr.value, diff: kpi.cvr.diff, format: 'percent' },
    { label: 'お気に入り', value: kpi.favorites.value, diff: kpi.favorites.diff },
    { label: '売上', value: kpi.revenue.value, format: 'currency' },
  ];

  const currentEventStats = {
    title: event.title,
    event_date: event.event_date ? event.event_date.substring(0, 10) : null,
    pv: kpi.pageviews.value,
    participants: kpi.participants.value,
    cvr: kpi.cvr.value,
  };

  const renderChart = () => {
    switch (activeChart) {
      case 'pv':
        return (
          <TimeSeriesChart
            data={charts.daily_pv}
            bars={[{ dataKey: 'pv', name: '日別PV' }]}
            lines={[{ dataKey: 'cumulative_participants', name: '累計参加者' }]}
          />
        );
      case 'pr':
        return (
          <TimeSeriesChart
            data={charts.daily_pr_consumed}
            bars={[{ dataKey: 'consumed', name: '消費ポイント' }]}
            lines={[{ dataKey: 'pv', name: 'PV' }]}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="o-dashboard">
      <div className="o-dashboard__header">
        <div>
          <h2 className="o-dashboard__title">イベントダッシュボード</h2>
          <div style={{ color: '#666', fontSize: '14px', marginTop: '4px' }}>
            {event.title} | {event.event_date ? new Date(event.event_date).toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}
          </div>
        </div>
      </div>

      <KpiCardGroup cards={kpiCards} />

      <div className="o-dashboard__section">
        <div className="o-chart-tabs">
          {CHART_TABS.map((tab) => (
            <button
              type="button"
              key={tab.key}
              className={`o-chart-tabs__tab${activeChart === tab.key ? ' o-chart-tabs__tab--active' : ''}`}
              onClick={() => setActiveChart(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {renderChart()}
      </div>

      <ParticipantDetail
        capacity={participants.capacity}
        joined={participants.joined}
        canceled={participants.canceled}
        fillRate={participants.fill_rate}
        cancelRate={participants.cancel_rate}
      />

      <PrPointSection {...pr_point} eventId={eventId} purchaseUrl={purchaseUrl} onDataChange={fetchData} />

      {past_events.length > 0 && (
        <PastEventComparison
          currentEvent={currentEventStats}
          pastEvents={past_events}
        />
      )}
    </div>
  );
};

EventDashboard.propTypes = {
  eventId: PropTypes.number.isRequired,
  circleId: PropTypes.number,
  purchaseUrl: PropTypes.string,
};

export default EventDashboard;
